import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getWheel, spinWheel } from "@/lib/api";
import { WheelSvg } from "@/components/spin/WheelSvg";
import { PrizeCelebration } from "@/components/spin/PrizeCelebration";
import { SpinForm } from "@/components/spin/SpinForm";

const SPUN_KEY = "stb_spun";

export const SpinToWin = () => {
  const [slots, setSlots] = useState([]);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [showPrize, setShowPrize] = useState(false);
  const [spun, setSpun] = useState(!!localStorage.getItem(SPUN_KEY));
  const wheelRef = useRef(null);
  const [player, setPlayer] = useState(null);

  useEffect(() => {
    getWheel().then((d) => setSlots(d.slots)).catch(() => {});
  }, []);

  const onSpin = async (name, email) => {
    setPlayer({ name, email });
    setSpinning(true);
    wheelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    try {
      const r = await spinWheel({ name, email });
      const seg = 360 / slots.length;
      const jitter = (Math.random() - 0.5) * seg * 0.6;
      setRotation((rot) => rot - (rot % 360) + 360 * 6 + (360 - (r.slot_index + 0.5) * seg) + jitter);
      setTimeout(() => {
        setSpinning(false);
        setResult(r);
        if (r.won || r.spins_left === 0) {
          setSpun(true);
          localStorage.setItem(SPUN_KEY, "1");
        }
        if (r.won) {
          confetti({ particleCount: 160, spread: 100, origin: { y: 0.5 }, colors: ["#D7263D", "#1F9D55", "#FFD700", "#FFFFFF"] });
          setShowPrize(true);
        }
      }, 5200);
    } catch (e) {
      setSpinning(false);
      toast.error(e?.response?.data?.detail || "Couldn't spin right now. Please try again.");
    }
  };

  if (!slots.length) return null;

  return (
    <section id="spin-to-win" className="relative py-16 sm:py-24 bg-candy-night border-y-4 border-candy-gold scroll-mt-24" data-testid="spin-to-win-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div ref={wheelRef} className="relative mx-auto w-full max-w-[340px] sm:max-w-[420px] aspect-square">
          <div className="absolute left-1/2 -top-3 -translate-x-1/2 z-10 w-0 h-0 border-l-[16px] border-r-[16px] border-t-[30px] border-l-transparent border-r-transparent border-t-candy-gold drop-shadow-[0_3px_0_#000]" data-testid="spin-pointer" />
          <motion.div
            className="w-full h-full rounded-full border-[6px] border-candy-gold shadow-[0_0_50px_rgba(215,38,61,0.55)]"
            animate={{ rotate: rotation }}
            transition={{ duration: 5, ease: [0.12, 0.8, 0.2, 1] }}
            data-testid="spin-wheel"
          >
            <WheelSvg slots={slots} />
          </motion.div>
        </div>

        <div>
          <div className="inline-flex items-center gap-2 bg-candy-pink text-white border-2 border-candy-gold rounded-full px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest">
            <Sparkles className="h-4 w-4" /> 3 chances to win
          </div>
          <h2 className="mt-4 font-spooky text-4xl sm:text-6xl text-candy-cream drop-shadow-[3px_3px_0_#000]" data-testid="spin-heading">
            Spin to Win!
          </h2>
          <p className="mt-3 text-base sm:text-lg text-candy-cream/90 font-medium">
            Spin our Christmas candy wheel for a chance at a free stocking, free candy, or money off your order.
          </p>
          {result && !result.won && result.spins_left > 0 ? (
            <div className="mt-6 bg-candy-card border-4 border-candy-teal rounded-2xl p-5" data-testid="spin-try-again">
              <p className="font-spooky text-3xl text-candy-gold">So close!</p>
              <p className="mt-1 text-base text-candy-cream font-medium" data-testid="spin-chances-left">
                You have {result.spins_left} {result.spins_left === 1 ? "chance" : "chances"} left.
              </p>
              <button
                data-testid="spin-again-btn"
                disabled={spinning}
                onClick={() => onSpin(player.name, player.email)}
                className="mt-4 w-full bg-candy-pink text-white font-extrabold uppercase text-lg tracking-wider py-4 border-4 border-black rounded-xl shadow-glow-orange disabled:opacity-70"
              >
                {spinning ? "Spinning…" : "Spin Again"}
              </button>
            </div>
          ) : result && !result.won ? (
            <div className="mt-6 bg-candy-card border-4 border-candy-teal rounded-2xl p-5" data-testid="spin-out-of-spins">
              <p className="font-spooky text-3xl text-candy-gold">So close!</p>
              <p className="mt-1 text-base text-candy-cream font-medium">Try again next time. Thanks for playing, and Merry Christmas!</p>
            </div>
          ) : result?.won ? (
            <button data-testid="spin-show-prize" onClick={() => setShowPrize(true)} className="mt-6 bg-candy-gold text-black font-bold uppercase tracking-wider py-3 px-6 border-4 border-black rounded-xl">
              Show my prize code
            </button>
          ) : spun ? (
            <p className="mt-6 text-base text-candy-cream font-medium bg-candy-card border-4 border-candy-teal rounded-2xl p-5" data-testid="spin-already">
              You've already played on this device. Up to 3 tries per email — thanks for playing!
            </p>
          ) : (
            <SpinForm onSpin={onSpin} spinning={spinning} />
          )}
        </div>
      </div>
      {showPrize && result?.won && <PrizeCelebration result={result} onClose={() => setShowPrize(false)} />}
    </section>
  );
};
